// src/components/mdx/client/Flowchart.client.tsx

"use client"

import { Fragment, type FC, type ReactNode } from "react"
import { motion } from "motion/react"
import { ArrowRight, ChevronRight } from "lucide-react"
import { cn } from "~/lib/utils"

/**
 * FlowchartItem represents a single item within a flowchart card
 * @property {ReactNode} icon - The icon to display (can be any React component)
 * @property {string} text - The text description for this item
 * @property {string} [value] - Optional value or metric to display (e.g. "↑", "10%", etc.)
 * @property {string} [valueColor] - Optional color for the value (e.g. "text-green-600" for positive, "text-red-600" for negative)
 */
export interface FlowchartItem {
  icon: ReactNode
  text: string
  value?: string
  valueColor?: string
}

/**
 * FlowchartStage represents a single stage/card in the flowchart
 * @property {string} title - The title of this stage
 * @property {string} [subtitle] - Optional subtitle for additional context
 * @property {FlowchartItem[]} items - Array of items to display in this stage
 * @property {string} [className] - Optional additional CSS classes
 */
export interface FlowchartStage {
  title: string
  subtitle?: string
  items: FlowchartItem[]
  className?: string
}

/**
 * FlowchartCardProps defines the props for the FlowchartCard component
 * @property {string} title - The title of the card
 * @property {string} [subtitle] - Optional subtitle for the card
 * @property {FlowchartItem[]} items - Array of items to display in the card
 * @property {number} delay - Animation delay in seconds
 * @property {string} [className] - Optional additional CSS classes
 * @property {string} [cardStyle] - Optional card style variant ("default", "gradient", "outline", "solid")
 * @property {string} [titleColor] - Optional color for the title
 * @property {string} [textColor] - Optional color for the text
 */
interface FlowchartCardProps {
  title: string
  subtitle?: string
  items: FlowchartItem[]
  delay: number
  className?: string
  cardStyle?: "default" | "gradient" | "outline" | "solid"
  titleColor?: string
  textColor?: string
  /** Wider max width when parent uses a multi-column grid (e.g. four-stage flow). */
  relaxedWidth?: boolean
}

/**
 * FlowchartCard component displays a single card in the flowchart
 */
const FlowchartCard: FC<FlowchartCardProps> = ({
  title,
  subtitle,
  items,
  delay,
  className,
  cardStyle = "default",
  titleColor = "text-blue-900",
  textColor = "text-blue-800",
  relaxedWidth = false,
}) => {
  // Determine card background style based on cardStyle prop
  const cardBgStyle = {
    default: "bg-gradient-to-br from-white to-blue-50",
    gradient: "bg-gradient-to-br from-white to-blue-50",
    outline: "bg-white border border-blue-200",
    solid: "bg-blue-50",
  }[cardStyle]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={cn(
        cardBgStyle,
        "rounded-xl shadow-lg p-6 flex flex-col items-start space-y-4 w-full min-w-0 md:min-w-[14rem] xl:min-w-[16rem]",
        relaxedWidth ? "max-w-md" : "max-w-sm",
        className
      )}
    >
      <div>
        <h3 className={cn("text-xl font-semibold", titleColor)}>{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <ul className={cn("text-sm space-y-3 w-full", textColor)}>
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="mt-0.5 flex-shrink-0 text-blue-600">{item.icon}</span>
            <span className="min-w-0 flex-1 leading-snug">{item.text}</span>
            {item.value && (
              <span className={cn("font-semibold", item.valueColor ?? "text-purple-600")}>
                {item.value}
              </span>
            )}
          </li>
        ))}
      </ul>
    </motion.div>
  )
}

/**
 * ArrowProps defines the props for the Arrow component
 * @property {ReactNode} [icon] - Optional custom icon to use instead of default
 * @property {string} [className] - Optional additional CSS classes
 * @property {string} [direction] - Direction of the arrow ("horizontal" or "vertical")
 * @property {string} [color] - Color of the arrow
 * @property {number} [size] - Size of the arrow icon
 */
interface ArrowProps {
  icon?: ReactNode
  className?: string
  direction?: "horizontal" | "vertical"
  color?: string
  size?: number
}

/**
 * Arrow component displays a connector between flowchart cards
 */
const Arrow: FC<ArrowProps> = ({
  icon,
  className,
  direction = "horizontal",
  color = "text-blue-400",
  size = 32,
}) => {
  // Default icons based on direction
  const defaultIcon =
    direction === "horizontal" ? (
      <ArrowRight className={color} size={size} />
    ) : (
      <ChevronRight className={cn(color, "transform rotate-90")} size={size} />
    )

  // Classes based on direction
  const directionClasses =
    direction === "horizontal"
      ? "hidden md:flex w-16 h-16"
      : "flex md:hidden h-16 w-16 transform rotate-90"

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "items-center justify-center",
        directionClasses,
        className
      )}
    >
      {icon ?? defaultIcon}
    </motion.div>
  )
}

/**
 * FlowchartProps defines the props for the Flowchart component
 * @property {FlowchartStage[]} stages - Array of stages to display in the flowchart
 * @property {string} [className] - Optional additional CSS classes
 * @property {string} [title] - Optional title for the entire flowchart
 * @property {string} [description] - Optional description for the flowchart
 * @property {ReactNode} [arrowIcon] - Optional custom arrow icon
 * @property {string} [cardStyle] - Optional card style variant ("default", "gradient", "outline", "solid")
 * @property {string} [titleColor] - Optional color for card titles
 * @property {string} [textColor] - Optional color for card text
 * @property {string} [arrowColor] - Optional color for arrows
 * @property {number} [arrowSize] - Optional size for arrow icons
 * @property {boolean} [animate] - Whether to animate the flowchart (default: true)
 * @property {string} [layout] - Layout direction ("horizontal" or "vertical")
 */
export interface FlowchartProps {
  stages: FlowchartStage[]
  className?: string
  title?: string
  description?: string
  arrowIcon?: ReactNode
  cardStyle?: "default" | "gradient" | "outline" | "solid"
  titleColor?: string
  textColor?: string
  arrowColor?: string
  arrowSize?: number
  animate?: boolean
  layout?: "horizontal" | "vertical"
  /**
   * When true and layout is horizontal, use a 2×2 grid from `lg` so four-stage flowcharts
   * get wider cards instead of one cramped row (optional; default keeps one row for shorter flows).
   */
  compactRows?: boolean
}

/**
 * Flowchart component displays a customizable, responsive flowchart
 * 
 * @example
 * ```tsx
 * import { Flowchart } from "~/components/mdx/client/Flowchart.client"
 * import { AlertTriangle, Check, X } from "lucide-react"
 * 
 * const MyFlowchart = () => (
 *   <Flowchart
 *     title="Process Overview"
 *     description="A visual representation of our workflow"
 *     stages={[
 *       {
 *         title: "Planning Phase",
 *         items: [
 *           { icon: <AlertTriangle size={18} />, text: "Identify Requirements", value: "1-2 weeks" },
 *           { icon: <Check size={18} />, text: "Resource Allocation", value: "Complete" },
 *         ]
 *       },
 *       {
 *         title: "Execution Phase",
 *         items: [
 *           { icon: <X size={18} />, text: "Development", value: "In Progress", valueColor: "text-orange-500" },
 *         ]
 *       }
 *     ]}
 *     cardStyle="gradient"
 *     arrowColor="text-indigo-400"
 *   />
 * )
 * ```
 */
const Flowchart: FC<FlowchartProps> = ({
  stages,
  className,
  title,
  description,
  arrowIcon,
  cardStyle = "default",
  titleColor,
  textColor,
  arrowColor = "text-blue-400",
  arrowSize = 32,
  animate = true,
  layout = "horizontal",
  compactRows = false,
}) => {
  // Determine layout classes
  const containerClasses =
    layout === "horizontal"
      ? compactRows
        ? "grid grid-cols-1 gap-8 sm:grid-cols-2"
        : "flex-col md:flex-row space-y-8 md:space-y-0 md:space-x-4"
      : "flex-col space-y-8"

  return (
    <div className={cn("my-12", className)}>
      {(title ?? description) && (
        <div className="mb-8 text-center">
          {title && <h2 className="text-2xl font-bold mb-2">{title}</h2>}
          {description && <p className="text-gray-600">{description}</p>}
        </div>
      )}
      <div
        className={cn(
          compactRows && layout === "horizontal"
            ? "items-stretch justify-items-center"
            : "flex items-center justify-center",
          containerClasses
        )}
      >
        {stages.map((stage, index) => (
          <Fragment key={index}>
            {index > 0 && !compactRows && (
              <Arrow
                icon={arrowIcon}
                direction={layout === "horizontal" ? "horizontal" : "vertical"}
                color={arrowColor}
                size={arrowSize}
              />
            )}
            <FlowchartCard
              title={stage.title}
              subtitle={stage.subtitle}
              items={stage.items}
              delay={animate ? index * 0.2 : 0}
              className={stage.className}
              cardStyle={cardStyle}
              titleColor={titleColor}
              textColor={textColor}
              relaxedWidth={compactRows && layout === "horizontal"}
            />
          </Fragment>
        ))}
      </div>
    </div>
  )
}

export default Flowchart
